/**
 * Types + maths for the HR PTO report.
 *
 * One gateway call (`enable-pto`) returns leave per (person, day, type) for a
 * year; every view here is a client-side rollup of those same rows, so
 * switching views costs nothing. See `docs/nos-gateway-endpoints.md`.
 */

/**
 * The five leave types NOS records. Order is the display order.
 *
 * `Observed Paid Holiday` is a company closure rather than personal leave, and
 * is the largest category by volume (768 entries in 2026 against 500 for
 * General PTO) — totalling absence without separating it overstates by more
 * than half, which is why the report shows every type as its own filterable
 * column rather than one "time off" number.
 */
export const PTO_TYPES = [
  "General PTO",
  "Sick PTO",
  "Exceptional Time Off",
  "Unpaid Time Off",
  "Observed Paid Holiday",
] as const;

export type PtoType = (typeof PTO_TYPES)[number];

/** Short labels for column headers, where the full names don't fit. */
export const PTO_TYPE_LABELS: Record<PtoType, string> = {
  "General PTO": "PTO",
  "Sick PTO": "Sick",
  "Exceptional Time Off": "Exceptional",
  "Unpaid Time Off": "Unpaid",
  "Observed Paid Holiday": "Holiday",
};

/**
 * Tint per leave type, so a chip reads as its type without a legend. Shared by
 * the PTO report, the person Time off tab and its filter chips — three surfaces
 * that must not drift apart on colour.
 */
export const PTO_TYPE_TONE: Record<PtoType, string> = {
  "General PTO": "bg-info-bg text-info-foreground",
  "Sick PTO": "bg-danger-bg text-danger-foreground",
  "Exceptional Time Off": "bg-warning-bg text-warning-foreground",
  "Unpaid Time Off": "bg-muted text-muted-foreground",
  "Observed Paid Holiday": "bg-success-bg text-success-foreground",
};

/**
 * A full working day in hours. 88% of personal leave is logged as exactly 8h
 * and a further 8% as 4h, so hours/8 is how HR reads these. It is an assumption
 * for anyone not on standard hours, which is why every figure keeps its exact
 * hours available on hover.
 */
export const HOURS_PER_DAY = 8;

/** One person's leave on one day, of one type. */
export interface PtoEntry {
  /** NOS PersonID, lower-cased so it can key against Enable people. */
  personId: string;
  personName: string;
  /** Date-only ISO (`YYYY-MM-DD`). */
  date: string;
  type: PtoType;
  hours: number;
}

/** A person seen in the report, enriched from Enable where we know them. */
export interface PtoPerson {
  personId: string;
  name: string;
  /** Null for the ~60 people NOS has but Enable has not synced yet. */
  departmentName: string | null;
  roleTeamName: string | null;
  /** Whether this person exists in Enable, and so can be linked to a profile. */
  inEnable: boolean;
}

export interface PtoReport {
  /** Date-only ISO bounds of the fetched range. */
  from: string;
  to: string;
  entries: PtoEntry[];
  people: PtoPerson[];
  /**
   * Leave awaiting approval, which is NOT represented in `entries` at all —
   * NOS writes no `TimeEntry` row until a request is approved. Deliberately
   * kept out of `entries` and every total derived from it: unapproved days
   * must not inflate the figures HR reads as leave taken.
   *
   * `null` means that read failed while the approved report succeeded (most
   * likely the request endpoint missing in this environment). The page says so
   * rather than showing an empty queue, which would read as "nothing pending".
   */
  pendingRequests: PtoRequest[] | null;
  /**
   * Whether this viewer is allowed to see the pending queue at all.
   *
   * Separate from `pendingRequests` because BOTH of that field's values already
   * mean something else: `[]` is "nothing is pending" and `null` is "we could
   * not check". Reusing either to mean "not for you" would tell a colleague
   * their queue is empty or broken when in fact it is simply none of their
   * business. False ⇒ the page omits the tab entirely.
   *
   * The requester's free-text reason and the approver's name live only on those
   * rows, so withholding the queue withholds them too — which is why there is
   * no separate flag for the reason.
   */
  canViewPending: boolean;
  /**
   * False when the server has no gateway credentials. The page then explains
   * itself rather than erroring — an environment without gateway access is
   * expected, not broken.
   */
  connected: boolean;
}

/** `12` → `"1.5"`, `8` → `"1"`. Days, trimmed of trailing zeroes. */
export function formatDays(hours: number): string {
  const days = hours / HOURS_PER_DAY;
  return String(Number(days.toFixed(2)));
}

/** `"12h"`, for the hover that backs every day figure. */
export function formatHours(hours: number): string {
  return `${Number(hours.toFixed(2))}h`;
}

/** Sums hours by type, keeping every type present so columns stay aligned. */
export function totalsByType(entries: PtoEntry[]): Record<PtoType, number> {
  const out = Object.fromEntries(PTO_TYPES.map((t) => [t, 0])) as Record<PtoType, number>;
  for (const e of entries) out[e.type] += e.hours;
  return out;
}

/** Total hours across every type. */
export function totalHours(entries: PtoEntry[]): number {
  let sum = 0;
  for (const e of entries) sum += e.hours;
  return sum;
}

/** `"2026-08-13"` → `"2026-08"`. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** Entries grouped by month key, earliest first. */
export function groupByMonth(entries: PtoEntry[]): { month: string; entries: PtoEntry[] }[] {
  const map = new Map<string, PtoEntry[]>();
  for (const e of entries) {
    const key = monthOf(e.date);
    const list = map.get(key) ?? [];
    list.push(e);
    map.set(key, list);
  }
  return [...map.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, list]) => ({ month, entries: list }));
}

/** Entries grouped by person, ordered by total hours descending. */
export function groupByPerson(entries: PtoEntry[]): { personId: string; entries: PtoEntry[] }[] {
  const map = new Map<string, PtoEntry[]>();
  for (const e of entries) {
    const list = map.get(e.personId) ?? [];
    list.push(e);
    map.set(e.personId, list);
  }
  return [...map.entries()]
    .map(([personId, list]) => ({ personId, entries: list }))
    .sort((a, b) => totalHours(b.entries) - totalHours(a.entries));
}

/** A group in the by-team view. `attributed` is false for the two fallbacks. */
interface PtoAttributeGroup {
  label: string;
  entries: PtoEntry[];
  headcount: number;
  /** False for the "not synced" and "not set" buckets, so the UI can dim them. */
  attributed: boolean;
}

/**
 * Entries grouped by a person attribute (department or role team), ordered by
 * total hours descending.
 *
 * The two reasons a person has no attribute are kept apart on purpose. Someone
 * NOS knows but Enable has never synced is a data-pipeline gap; someone Enable
 * has but nobody has placed in a department is an admin gap. Collapsing both
 * into one "Unknown" bucket hides which is which — and today that matters,
 * because every NOS-synced person falls in the second case: the sync only
 * populates columns Enable already had, and department is set by hand in
 * Enable, so this view stays mostly unattributed until someone fills it in.
 */
export function groupByAttribute(
  entries: PtoEntry[],
  people: PtoPerson[],
  attribute: "departmentName" | "roleTeamName",
  labels: { notSet: string; notSynced: string },
): PtoAttributeGroup[] {
  const byId = new Map(people.map((p) => [p.personId, p]));
  const map = new Map<string, { entries: PtoEntry[]; people: Set<string>; attributed: boolean }>();

  for (const e of entries) {
    const found = byId.get(e.personId);
    const value = found?.[attribute] ?? null;
    const attributed = value !== null;
    const label = value ?? (found?.inEnable ? labels.notSet : labels.notSynced);
    const group = map.get(label) ?? { entries: [], people: new Set<string>(), attributed };
    group.entries.push(e);
    group.people.add(e.personId);
    map.set(label, group);
  }

  return [...map.entries()]
    .map(([label, g]) => ({
      label,
      entries: g.entries,
      headcount: g.people.size,
      attributed: g.attributed,
    }))
    .sort((a, b) => {
      // Unattributed buckets sink to the bottom whatever their size, so a large
      // "not set" group can't crowd out the real departments.
      if (a.attributed !== b.attributed) return a.attributed ? -1 : 1;
      return totalHours(b.entries) - totalHours(a.entries);
    });
}

/** Distinct dates in a set of entries, earliest first. */
export function distinctDates(entries: PtoEntry[]): string[] {
  return [...new Set(entries.map((e) => e.date))].sort();
}

/** Local `YYYY-MM-DD` for today — date-only values sort and compare as strings. */
export function todayKey(from: Date = new Date()): string {
  const m = String(from.getMonth() + 1).padStart(2, "0");
  const d = String(from.getDate()).padStart(2, "0");
  return `${from.getFullYear()}-${m}-${d}`;
}

/**
 * The days of a month laid out as calendar weeks, Monday-first, padded with
 * nulls so every week has seven cells.
 *
 * Built from `Date` arithmetic rather than by counting, so month lengths and
 * leap years take care of themselves.
 */
export function calendarWeeks(month: string): (string | null)[][] {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return [];
  const first = new Date(y, m - 1, 1);
  const daysInMonth = new Date(y, m, 0).getDate();
  // getDay() is Sunday-0; shift so Monday is the first column.
  const lead = (first.getDay() + 6) % 7;

  const cells: (string | null)[] = Array<string | null>(lead).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** `"2026-08"` → `"August 2026"`. */
export function formatMonth(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/**
 * `"2026-08-13"` -> `"Thu 13 Aug"`. Named for the PTO report rather than
 * `formatDate`, which is the general formatter in lib/utils.ts — two exported
 * `formatDate`s with different output was a trap for whoever imported the
 * wrong one.
 */
export function formatPtoDay(date: string): string {
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

/**
 * Working days (Mon–Fri) in an inclusive date range, or 0 if `end` precedes
 * `start`.
 *
 * Used to size a PENDING leave request, which NOS stores only as a range plus
 * hours-per-day — it expands a range into real booked days when the request is
 * approved, not before. Weekends are excluded because NOS certainly never books
 * them; company holidays are NOT deducted, because which dates those are is not
 * knowable from a request. So this is a good estimate and the UI labels it as
 * one; an approved request's days come from real entries instead.
 */
export function weekdaysBetween(start: string, end: string): number {
  const from = parseDateKey(start);
  const to = parseDateKey(end);
  if (from === null || to === null || to < from) return 0;

  let days = 0;
  for (const d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) days++;
  }
  return days;
}

/** `"2026-08-27"` → local midnight `Date`, or null if unparseable. */
function parseDateKey(key: string): Date | null {
  const [y, m, d] = key.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/**
 * The non-approved request states NOS has. Approved requests never reach Enable
 * this way — approved leave arrives per-day from `TimeEntry` instead, which is
 * the accurate shape (see `PtoRequest`).
 */
export const PTO_REQUEST_STATUSES = ["pending", "indraft", "rejected", "requestdelete"] as const;

export type PtoRequestStatus = (typeof PTO_REQUEST_STATUSES)[number];

/**
 * Which states the report surfaces as "pending".
 *
 * Only `pending` — a request submitted and waiting on an approver. `indraft` is
 * someone's unsent draft and may never be submitted; `rejected` and
 * `requestdelete` are decided outcomes, not things awaiting a decision.
 * Widening this is a one-line change here: the gateway endpoint already returns
 * all four, so nothing outside this repo has to move.
 */
const SHOWN_STATUSES: readonly PtoRequestStatus[] = ["pending"];

/**
 * One leave REQUEST, as opposed to booked leave.
 *
 * A range plus hours-per-day, not a set of days, because that is all NOS holds
 * until the request is approved — it expands the range into real booked days,
 * skipping weekends and company holidays, at approval time. So a pending
 * request cannot honestly be drawn on a calendar, and its `days` is an
 * estimate.
 */
export interface PtoRequest {
  /** NOS PTORequestID, as returned (upper-cased) — identity only, never joined. */
  id: string;
  /** NOS PersonID, lower-cased so it can key against Enable people. */
  personId: string;
  personName: string;
  type: PtoType;
  status: PtoRequestStatus;
  /** NOS's own wording for the status, e.g. "Pending Approval". */
  statusLabel: string;
  /** Date-only ISO (`YYYY-MM-DD`), inclusive. */
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  /**
   * Working days in the range — an ESTIMATE. Weekends are excluded; company
   * holidays cannot be, so a request spanning one reads high by a day. The UI
   * labels it as approximate.
   */
  days: number;
  /**
   * NOS PersonID of the named approver, lower-cased so it keys against Enable
   * people. Carried alongside the name so "awaiting YOUR approval" is decided
   * by identity rather than by matching display names.
   */
  approverPersonId: string | null;
  approverName: string | null;
  /** Date-only ISO the request was raised, where NOS recorded one. */
  requestedOn: string | null;
  /**
   * Free text written by the requester. Routinely carries medical and family
   * detail, so it is served to admins only and omitted server-side — never
   * merely hidden in the UI — for anyone else.
   */
  description: string | null;
  /** Whether this person exists in Enable, and so can be linked to a profile. */
  inEnable: boolean;
}

/** Narrows a NOS status enum to the union, or null if NOS adds one. */
export function asPtoRequestStatus(value: string): PtoRequestStatus | null {
  return (PTO_REQUEST_STATUSES as readonly string[]).includes(value)
    ? (value as PtoRequestStatus)
    : null;
}

/** The subset of requests the report shows as awaiting approval. */
export function pendingOnly(requests: PtoRequest[]): PtoRequest[] {
  return requests.filter((r) => SHOWN_STATUSES.includes(r.status));
}
